import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, DatePicker, Input, InputNumber, Modal, Select, Space, Typography, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { recordPaymentOrderRefund, recordRefund, refundQueries } from '../../../../../Actions'
import {
  followUpActionOf,
  PAYMENT_ORDER_REASONS,
  PENALTY_BRACKET_LABEL,
  PenaltyBracket,
  RecordRefundRequest,
  REFUND_CHANNEL_LABEL,
  REFUND_FOLLOW_UP_ACTION_LABEL,
  REFUND_REASON_LABEL,
  RefundChannel,
  RefundReason,
  RefundTarget,
  RefundTargetView,
  refundTargetKey,
  refundTargetLabel,
  SELECTABLE_PENALTY_BRACKETS,
} from '../../../../../types/refund'
import { apiErrorMessage } from '../../../../../utils/apiError'
import useInvalidateRefunds from './useInvalidateRefunds'

type Props = {
  open: boolean
  workspaceId: number
  /** 청구서 또는 크레딧 팩 결제 건. 종류에 따라 현황·기록 API 가 갈린다(D-121) */
  target: RefundTarget | null
  onClose: () => void
}

/**
 * 청구서 수동 기록으로 넣을 수 있는 사유.
 *
 * 🔴 **후속 조치가 붙는 사유는 여기 없다.** 이 경로는 행을 곧바로 처리 완료로 만드는데, 그러면
 * 확정 단계를 건너뛰어 조치가 발화하지 않는다. 서버는 그 행을 `FAILED` 로 표시해 재시도 대상으로
 * 남기므로(`RefundFollowUpService`), 구독취소·청약철회·되감기 사유를 이리로 넣으면 대장에 실패
 * 행이 쌓인다. 그쪽은 산정 경로(`RefundQuoteModal`)와 되감기 경로(`RollbackIntentModal`) 소관이다.
 *
 * 단건 크레딧팩(`CREDIT_PACK`)도 뺐다 — 크레딧 팩 결제는 청구서가 아니라 결제 건이다(D-121).
 * 청구서에 그 사유를 붙이면 구독 결제에서 돈이 빠진 것으로 기록되고, 소멸도 돌지 않는다.
 */
const INVOICE_MANUAL_REASONS: RefundReason[] = ['COMPANY_FAULT_TERMINATION', 'POST_EXPIRY_DISCRETION', 'OTHER']

/** 대상별 사유. 결제 건은 서버가 받는 다섯 값 그대로다 — 어느 것이든 후속 조치는 남은 크레딧 소멸이다 */
const manualReasonsOf = (target: RefundTarget | null): RefundReason[] =>
  target?.kind === 'PAYMENT_ORDER' ? PAYMENT_ORDER_REASONS : INVOICE_MANUAL_REASONS

const isPaymentOrder = (target: RefundTarget | null) => target?.kind === 'PAYMENT_ORDER'

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)

/** BE 는 `LocalDateTime` 을 받는다. 날짜만 보내면 파싱에서 끊긴다 */
const toLocalDateTime = (value: Dayjs) => value.format('YYYY-MM-DD[T]HH:mm:ss')

const channelOptions = (Object.keys(REFUND_CHANNEL_LABEL) as RefundChannel[]).map((value) => ({
  value,
  label: REFUND_CHANNEL_LABEL[value],
}))

/**
 * 수동 기록 — 회사 귀책·재량 처리 등 (화면 명세 4절 · aipark-front #266 P4 · Phase 2 P5 #280)
 *
 * 산정 API 가 없는 환불을 **이미 실행한 결과로** 남긴다. 접수 행이 아니라 처리 완료 행이
 * 곧바로 만들어진다(`status` 생략 = `COMPLETED`, 이 API 의 종전 계약).
 *
 * 🔴 그래서 **되돌릴 수 없다.** 처리 완료 행은 철회할 수 없고 정정은 반대 방향의 새 기록이다.
 *
 * 크레딧 팩 결제 건이면 확정 단계를 건너뛰므로 소멸이 아직 돌지 않은 상태로 기록된다. 서버는 그 결제
 * 건에 남은 크레딧으로 후속 조치 상태를 정한다 — 남은 것이 없으면 "실행 안 함", 남았거나 알 수
 * 없으면 "실패" 로 남고 재시도가 소멸을 실행한다(aivatar #540).
 */
export default function RecordRefundModal({ open, workspaceId, target, onClose }: Props) {
  const [reason, setReason] = useState<RefundReason | undefined>()
  const [amount, setAmount] = useState<number | null>(null)
  const [penalty, setPenalty] = useState<number | null>(null)
  const [refundedAt, setRefundedAt] = useState<Dayjs | null>(null)
  const [channel, setChannel] = useState<RefundChannel | undefined>()
  const [actualCancelAmount, setActualCancelAmount] = useState<number | null>(null)
  const [pgTransactionNo, setPgTransactionNo] = useState('')
  const [calculation, setCalculation] = useState('')
  const [memo, setMemo] = useState('')
  /**
   * 위약금 근거 3값. 구독취소·되감기는 서버 산정이 채우지만 이 경로에는 산정 API 가 없어
   * **담당자가 직접 넣는다**(화면 구성 v3 S3 · aivatar #506). 넣지 않으면 대장의 구간·요율·
   * 사용률 칸이 영구히 빈다.
   *
   * 화면은 % 로 묻고 서버에는 비율로 보낸다(0.1 = 10%).
   */
  const [penaltyBracket, setPenaltyBracket] = useState<PenaltyBracket | undefined>()
  const [penaltyRatePercent, setPenaltyRatePercent] = useState<number | null>(null)
  const [usageRatioPercent, setUsageRatioPercent] = useState<number | null>(null)
  /** 정정 대상. 같은 결제 건의 **철회된** 행만 지목할 수 있다(서버가 막는다) */
  const [supersedesRefundId, setSupersedesRefundId] = useState<number | undefined>()

  const invalidate = useInvalidateRefunds(workspaceId)
  /** 폼 초기화 기준. 종류까지 넣는다 — 청구서 23 과 결제 건 23 을 같은 대상으로 보면 앞 입력이 남는다 */
  const targetKey = target ? refundTargetKey(target) : null

  useEffect(() => {
    if (!open) return
    setReason(undefined)
    setAmount(null)
    setPenalty(null)
    setRefundedAt(dayjs())
    setChannel(undefined)
    setActualCancelAmount(null)
    setPgTransactionNo('')
    setCalculation('')
    setMemo('')
    setPenaltyBracket(undefined)
    setPenaltyRatePercent(null)
    setUsageRatioPercent(null)
    setSupersedesRefundId(undefined)
  }, [open, targetKey])

  /** 남은 한도를 미리 보여준다. 서버도 막지만(합계 ≤ 결제액) 눌러보고 알 일이 아니다 */
  const { data: invoiceView } = useQuery({
    ...refundQueries.invoiceView(target?.kind === 'INVOICE' ? target.invoiceId : 0),
    enabled: open && target?.kind === 'INVOICE',
  })
  const { data: paymentOrderView } = useQuery({
    ...refundQueries.paymentOrderView(target?.kind === 'PAYMENT_ORDER' ? target.paymentOrderId : 0),
    enabled: open && target?.kind === 'PAYMENT_ORDER',
  })
  const view: RefundTargetView | undefined = target?.kind === 'PAYMENT_ORDER' ? paymentOrderView : invoiceView

  const remaining = view ? view.price - view.refundedTotal - view.unsettledTotal : null

  /**
   * 정정 대상 후보. **철회된 행만** 고를 수 있고 같은 결제 건 안에서만 성립한다 — 서버가 둘 다
   * 400 으로 막으므로 목록 자체를 그 규칙으로 좁힌다(aivatar #502).
   */
  const withdrawnRefunds = (view?.refunds ?? []).filter((refund) => refund.status === 'WITHDRAWN')
  const overLimit = remaining != null && amount != null && amount > remaining

  /** 🔴 대상 종류로 API 를 가른다 — 결제 건 id 로 청구서 API 를 부르면 남의 청구서에 환불이 붙는다 */
  const recordTo = (to: RefundTarget, body: RecordRefundRequest) =>
    to.kind === 'INVOICE' ? recordRefund(to.invoiceId, body) : recordPaymentOrderRefund(to.paymentOrderId, body)

  const { mutateAsync: record, isPending } = useMutation({
    mutationFn: () =>
      recordTo(target!, {
        // status 생략 = 처리 완료 행. 이 경로는 "이미 실행한 환불" 을 남긴다
        amount: amount!,
        penalty,
        reason: reason!,
        channel,
        calculation: calculation.trim(),
        refundedAt: toLocalDateTime(refundedAt!),
        pgTransactionNo: pgTransactionNo.trim() || undefined,
        memo: memo.trim() || undefined,
        // 생략하면 서버가 산정액과 같다고 본다. 갈리는 경우에만 담당자가 따로 적는다
        actualCancelAmount: actualCancelAmount ?? undefined,
        penaltyBracket,
        // % → 비율. 서버가 다시 퍼센트로 바꿔 저장하므로 여기서 100 을 곱하면 100배가 된다
        penaltyRate: penaltyRatePercent == null ? undefined : penaltyRatePercent / 100,
        usageRatio: usageRatioPercent == null ? undefined : usageRatioPercent / 100,
        supersedesRefundId,
      }),
  })

  const ready = !!reason && !!amount && amount > 0 && !!refundedAt && !!channel && !!calculation.trim()

  const submit = async () => {
    try {
      const result = await record()
      message.success(`환불을 기록했습니다. 이 결제 건의 환불 합계는 ${money(result.refundedTotal)} 입니다.`)
    } catch (e) {
      message.error(
        apiErrorMessage(e) ??
          '기록하지 못했습니다. 결제 성공 건인지, 환불 합계가 결제액을 넘지 않는지, 처리일이 미래가 아닌지 확인하세요. (서버가 사유를 주지 않았습니다)',
      )
      return
    }

    await invalidate()
    onClose()
  }

  const confirmAndSubmit = () => {
    Modal.confirm({
      title: '처리 완료 기록으로 남길까요?',
      width: 520,
      content: (
        <Space direction="vertical">
          <span>
            이 경로는 <b>접수가 아니라 처리 완료</b> 행을 만듭니다 — 이미 돈이 나간 건을 기록하는
            것입니다.
          </span>
          <span>처리 완료 행은 철회할 수 없습니다. 정정은 반대 방향의 새 기록입니다.</span>
          {isPaymentOrder(target) ? (
            <span>
              이 결제 건에 남은 크레딧이 있으면 후속 조치가 <b>실패</b>로 남습니다 — 대장 상세의 재시도가 남은
              크레딧을 소멸시킵니다.
            </span>
          ) : null}
        </Space>
      ),
      okText: '기록',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: submit,
    })
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      title="환불 기록 추가 (수동)"
      okText="기록"
      okButtonProps={{ danger: true, loading: isPending, disabled: !ready || overLimit }}
      cancelText="닫기"
      onOk={confirmAndSubmit}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="이미 실행한 환불을 기록하는 화면입니다"
        description="접수 행이 아니라 처리 완료 행이 바로 만들어집니다. 아직 돈이 나가지 않았다면 결제 건 목록의 '환불' 로 산정해 접수하세요."
      />

      {target ? (
        <Typography.Paragraph type="secondary">
          대상 {refundTargetLabel(target)}
          {view ? ` · ${money(view.price)}` : ''}
          {remaining != null ? ` · 남은 한도 ${money(remaining)}` : ''}
        </Typography.Paragraph>
      ) : null}

      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <span>
          환불 사유 <Typography.Text type="danger">*</Typography.Text>
        </span>
        <Select
          style={{ width: '100%' }}
          placeholder="사유를 고르세요"
          value={reason}
          onChange={setReason}
          options={manualReasonsOf(target).map((value) => ({
            value,
            label: `${REFUND_REASON_LABEL[value]} — ${REFUND_FOLLOW_UP_ACTION_LABEL[followUpActionOf(value, target)]}`,
          }))}
        />
        {/* 왜 목록이 좁은지 담당자가 알아야 다른 사유를 찾아 헤매지 않는다 */}
        <Typography.Text type="secondary">
          {isPaymentOrder(target)
            ? '구독 사유는 크레딧 팩 결제 건에 쓸 수 없습니다. 어느 사유든 후속 조치는 이 결제 건의 남은 크레딧 소멸입니다.'
            : '구독취소·청약철회·되감기는 이 경로에 없습니다 — 후속 조치가 붙는 사유라 산정·접수 경로로 처리해야 합니다. 크레딧 팩 환불은 크레딧 팩 결제 건 행에서 기록하세요.'}
        </Typography.Text>

        <span>
          환불액 (부가세 포함) <Typography.Text type="danger">*</Typography.Text>
        </span>
        <InputNumber style={{ width: '100%' }} min={1} addonAfter="원" value={amount} onChange={setAmount} />
        {overLimit ? (
          <Alert
            type="error"
            showIcon
            message="남은 한도를 넘습니다"
            description={`이 결제 건에 이미 걸려 있는 금액을 빼면 ${money(remaining)} 까지 기록할 수 있습니다. 서버가 거부합니다.`}
          />
        ) : null}

        <span>위약금 (선택)</span>
        <InputNumber style={{ width: '100%' }} min={0} addonAfter="원" value={penalty} onChange={setPenalty} />

        <span>위약금 구간 (선택)</span>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder="근거 조항. 위약금이 0원이어도 남겨두면 사후 대조가 됩니다"
          value={penaltyBracket}
          options={SELECTABLE_PENALTY_BRACKETS.map((value) => ({
            value,
            label: PENALTY_BRACKET_LABEL[value],
          }))}
          onChange={setPenaltyBracket}
        />

        <Space style={{ width: '100%' }} size={8}>
          <Space direction="vertical" size={4}>
            <span>위약금 요율 (선택)</span>
            <InputNumber
              min={0}
              max={100}
              addonAfter="%"
              value={penaltyRatePercent}
              onChange={setPenaltyRatePercent}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <span>사용률 (선택)</span>
            <InputNumber
              min={0}
              max={100}
              addonAfter="%"
              placeholder="단건 공제 근거"
              value={usageRatioPercent}
              onChange={setUsageRatioPercent}
            />
          </Space>
        </Space>
        {/* 금액만 남기면 같은 0원이 청약철회인지 종료 선택권인지 사후에 갈리지 않는다(D-66) */}
        <Typography.Text type="secondary">
          구간·요율·사용률은 서버 산정이 없는 이 경로에서만 담당자가 넣습니다. 금액이 0원이어도
          근거 조항은 남겨두세요.
        </Typography.Text>

        <span>
          산정 근거 <Typography.Text type="danger">*</Typography.Text>
        </span>
        <Input.TextArea
          rows={3}
          maxLength={1000}
          showCount
          value={calculation}
          onChange={(e) => setCalculation(e.target.value)}
          placeholder="예) 단건 크레딧팩 10,000크레딧 중 3,000 사용 — 미사용분 70% 환급"
        />
        {/* 서버는 선택값이지만 화면에서 필수로 받는다. 이 경로에는 산정 API 가 없어서
            금액이 어떻게 나왔는지에 대한 설명이 이 문장 하나뿐이다 */}
        <Typography.Text type="secondary">
          이 경로에는 산정 API 가 없습니다. 금액 근거가 이 문장뿐이라 비워둘 수 없습니다.
        </Typography.Text>

        <span>
          처리일 <Typography.Text type="danger">*</Typography.Text>
        </span>
        {/**
          * 🔴 **하한을 두지 않는다.** 확정 폼(`RefundDetailDrawer`)은 결제일을 하한으로 막지만
          * 여기는 막지 않는 것이 맞다 — 서버 규칙이 두 경로에서 다르다.
          *
          * - 확정(`validateCompletion`): 미래 금지 **+ 결제일 이후**(`requireProcessedAfterPayment`).
          *   그 경로의 처리일은 **비활성 진입일**이 되므로 결제 전에 구독이 끝난 것으로 기록되면 안 된다
          * - 수동 기록(`validateAgainstStatus`): 미래 금지**만**. 이 경로는 후속 조치를 발화시키지
          *   않아 처리일이 진입일이 아니다
          *
          * 일관성을 이유로 하한을 붙이면 서버가 받는 입력을 화면이 막는다 — 회사 귀책처럼 운영
          * 판단이 들어가는 건에서 정당한 값을 넣을 수 없게 된다.
          */}
        <DatePicker
          showTime={{ format: 'HH:mm' }}
          format="YYYY-MM-DD HH:mm"
          style={{ width: '100%' }}
          value={refundedAt}
          disabledDate={(current) => current.isAfter(dayjs(), 'day')}
          onChange={setRefundedAt}
        />
        {/* 달력이 범위 밖을 조용히 되돌리면 담당자가 왜 막히는지 모른다(화면 구성 v3 S8) */}
        <Typography.Text type="secondary">
          오늘까지만 고를 수 있습니다 — 이미 실행한 환불의 <b>실제 취소일</b>을 넣으세요.
        </Typography.Text>

        <span>
          처리 경로 <Typography.Text type="danger">*</Typography.Text>
        </span>
        <Select
          style={{ width: '100%' }}
          placeholder="돈이 실제로 나간 경로"
          value={channel}
          options={channelOptions}
          onChange={setChannel}
        />

        <span>실제 취소 금액 (선택)</span>
        <InputNumber
          style={{ width: '100%' }}
          min={1}
          addonAfter="원"
          placeholder="비워두면 환불액과 같다고 봅니다"
          value={actualCancelAmount}
          onChange={setActualCancelAmount}
        />

        <span>결제대행사 거래번호 (선택)</span>
        <Input value={pgTransactionNo} onChange={(e) => setPgTransactionNo(e.target.value)} />

        <span>메모 (선택)</span>
        <Input.TextArea rows={2} maxLength={500} value={memo} onChange={(e) => setMemo(e.target.value)} />

        <span>정정 대상 (선택)</span>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={
            withdrawnRefunds.length
              ? '이 기록이 정정하는 원 환불 기록'
              : '이 결제 건에 철회된 기록이 없습니다'
          }
          disabled={!withdrawnRefunds.length}
          value={supersedesRefundId}
          options={withdrawnRefunds.map((refund) => ({
            value: refund.id,
            label: `#${refund.id} · ${money(refund.amount)} · ${REFUND_REASON_LABEL[refund.reason] ?? refund.reason}`,
          }))}
          onChange={setSupersedesRefundId}
        />
        {/* 행을 지우지 않는 대장에서 "어느 쪽이 유효한 기록인가" 를 잇는 유일한 값이다 */}
        <Typography.Text type="secondary">
          철회된 행만 지목할 수 있습니다. 연결하면 대장과 상세에서 두 행이 서로를 가리킵니다.
        </Typography.Text>
      </Space>
    </Modal>
  )
}
